import { useState, useCallback, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTimeSlots, useBookTimeSlot } from '../hooks/timeslotHook'
import { useRecaptcha } from '@/modules/shared/hooks/use-recaptcha'
import { Calendar, CheckCircle } from "lucide-react"
import { toast } from "sonner"
import TimelineCard from './components/TimelineCard'

export default function InterviewTimeslot() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null)
  const [isBooking, setIsBooking] = useState(false)
  const { data: timeSlotsData, isLoading, error } = useTimeSlots(token || '')
  const bookTimeSlot = useBookTimeSlot(token!)
  const { getToken: getCaptchaToken } = useRecaptcha()

  const candidateSlot = timeSlotsData
  const allAvailableSlots = useMemo(() => candidateSlot?.availableSlots || [], [candidateSlot?.availableSlots])

  const handleSave = useCallback(async () => {
    if (selectedSlot !== null && token) {
      setIsBooking(true)
      try {
        const captchaToken = await getCaptchaToken("interview_schedule")
        await bookTimeSlot.mutateAsync({ timelineId: selectedSlot, captchaToken })

        const confirmedSlot = allAvailableSlots.find((slot) => slot.id === selectedSlot)

        navigate('/booking-confirmed', {
          state: {
            candidateName: candidateSlot?.candidateName,
            candidateEmail: candidateSlot?.candidateEmail,
            interviewTitle: candidateSlot?.interviewTitle,
            startTime: confirmedSlot?.startTime,
            endTime: confirmedSlot?.endTime,
            description: `${candidateSlot?.notes}`
          },
          replace: true
        })
      } catch (_error) {
        toast.error('Booking failed. Please try again.')
        setIsBooking(false)
      }
    }
  }, [selectedSlot, token, bookTimeSlot, navigate, allAvailableSlots, candidateSlot, getCaptchaToken])

  if (!token) {
    return (
      <section className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-foreground mb-4">Invalid URL</h2>
          <p className="text-muted-foreground">Token is missing from URL</p>
        </div>
      </section>
    )
  }

  if (isLoading) {
    return (
      <section className="min-h-screen py-12 sm:py-16 md:py-20 bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1b237a] mx-auto mb-4" />
          <p className="text-lg text-muted-foreground">Loading available timeslots...</p>
        </div>
      </section>
    )
  }

  if (error) {
    return (
      <section className="min-h-screen py-12 sm:py-16 md:py-20 bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-2">Unable to load timeslots</h2>
          <p className="text-muted-foreground mb-6">Please refresh or contact support.</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-[#1b237a] text-white px-8 py-3 rounded-xl font-semibold hover:bg-[#1a2a8f] transition-colors"
          >
            Retry
          </button>
        </div>
      </section>
    )
  }

  return (
    <section className="min-h-screen py-12 sm:py-16 md:py-20 bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-16 max-w-2xl mx-auto">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-[#1b237a] to-[#1a2a8f] bg-clip-text text-transparent mb-6 sm:mb-8">
            Hey {candidateSlot?.candidateName || 'Candidate'},
          </h1>
          <p className="text-lg sm:text-xl text-muted-foreground px-4">
            Please select a time slot for your interview for the <strong>{candidateSlot?.vacancyTitle}</strong> position at <strong>{candidateSlot?.organizationName}</strong>
          </p>
        </div>

        <div className="max-w-3xl mx-auto space-y-4 sm:space-y-6 mb-8 sm:mb-12">
          {allAvailableSlots.length === 0 ? (
            <div className="text-center py-16">
              <Calendar className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-xl font-medium text-muted-foreground">No timeslots available</p>
              <p className="text-muted-foreground mt-2">Please contact support or try again later</p>
            </div>
          ) : (
            allAvailableSlots.map((slot) => (
              <TimelineCard
                key={slot.id}
                slot={slot}
                selectedSlot={selectedSlot}
                onSelect={setSelectedSlot}
              />
            ))
          )}
        </div>

        <div className="text-center space-y-4">
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              onClick={handleSave}
              disabled={selectedSlot === null || allAvailableSlots.length === 0 || isBooking}
              className={`inline-flex items-center gap-2 sm:gap-3 px-8 sm:px-12 py-4 sm:py-6 rounded-2xl sm:rounded-3xl text-lg sm:text-xl font-semibold shadow-2xl transform transition-all duration-300 group ${selectedSlot !== null && allAvailableSlots.length > 0 && !isBooking
                ? 'bg-gradient-to-r from-[#1b237a] to-[#1a2a8f] hover:from-[#1a2a8f] hover:to-[#1b237a] text-white hover:-translate-y-2 cursor-pointer'
                : 'bg-muted text-muted-foreground cursor-not-allowed'
                }`}
            >
              {isBooking ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                  Booking...
                </>
              ) : (
                <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 group-hover:scale-110 transition-transform" />
              )}
              {isBooking ? 'Booking...' : 'Save Time Slot'}
            </button>
          </div>

          {selectedSlot === null && allAvailableSlots.length > 0 && (
            <p className="mt-4 text-sm sm:text-base text-muted-foreground">
              Please select a time slot to continue
            </p>
          )}
          {allAvailableSlots.length === 0 && !isLoading && (
            <p className="mt-4 text-sm sm:text-base text-muted-foreground">
              No timeslots available at the moment
            </p>
          )}
        </div>
      </div>
    </section>
  )
}
